import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { LANGUAGES } from "@/i18n";
import api from "@/lib/api";
import {
  Gauge, ClipboardText, ListChecks, Buildings, Files, ChartBar,
  ShieldCheck, Bell, Users as UsersIcon, SignOut, Hospital, CaretDown,
  GearSix, KeyReturn, Translate,
} from "@phosphor-icons/react";

const ALL_NAV = [
  { to: "/", labelKey: "dashboard", icon: Gauge, roles: ["admin", "ops_manager", "supervisor", "auditor"] },
  { to: "/worklist", labelKey: "worklist", icon: ClipboardText, roles: ["staff", "admin", "supervisor"] },
  { to: "/verifications", labelKey: "verifications", icon: ShieldCheck, roles: ["supervisor", "ops_manager", "admin"] },
  { to: "/masters", labelKey: "masters", icon: Buildings, roles: ["admin", "ops_manager"] },
  { to: "/checklists", labelKey: "checklists", icon: ListChecks, roles: ["admin", "ops_manager"] },
  { to: "/reports", labelKey: "reports", icon: ChartBar, roles: ["admin", "ops_manager", "auditor"] },
  { to: "/audit-logs", labelKey: "audit_logs", icon: Files, roles: ["admin", "auditor", "ops_manager"] },
  { to: "/users", labelKey: "users", icon: UsersIcon, roles: ["admin"] },
  { to: "/roles", labelKey: "roles", icon: KeyReturn, roles: ["admin"] },
  { to: "/settings", labelKey: "settings", icon: GearSix, roles: ["admin", "ops_manager", "supervisor", "staff", "auditor"] },
];

function roleLabel(r) {
  return ({ admin: "Administrator", ops_manager: "Ops Manager", supervisor: "Supervisor", staff: "Housekeeping", auditor: "Auditor" })[r] || r;
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { setBrand } = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState(() => localStorage.getItem("hk_hospital") || "");

  const nav = ALL_NAV.filter((n) => n.roles.includes(user?.role));

  useEffect(() => {
    const loadNotifs = async () => {
      try { const { data } = await api.get("/notifications"); setNotifs(data); } catch (_) {}
    };
    const loadHospitals = async () => {
      try {
        const { data } = await api.get("/masters/hospitals");
        // Scope: if user has hospital_ids, restrict the picker to those
        const filtered = user?.hospital_ids?.length ? data.filter((h) => user.hospital_ids.includes(h.id)) : data;
        setHospitals(filtered);
        if (!selectedHospitalId && filtered.length > 0) {
          setSelectedHospitalId(filtered[0].id);
          localStorage.setItem("hk_hospital", filtered[0].id);
        }
      } catch (_) {}
    };
    loadNotifs(); loadHospitals();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // Apply per-hospital brand whenever the selected hospital changes
  useEffect(() => {
    const h = hospitals.find((x) => x.id === selectedHospitalId);
    setBrand(h ? { accent_color: h.accent_color, logo_url: h.logo_url, display_name: h.display_name } : null);
  }, [selectedHospitalId, hospitals, setBrand]);

  const unread = notifs.filter((n) => !n.read).length;
  const selectedHospital = hospitals.find((h) => h.id === selectedHospitalId);
  const brandDisplay = selectedHospital?.display_name || selectedHospital?.name;
  const brandLogo = selectedHospital?.logo_url;

  const onSignOut = async () => { await logout(); navigate("/login"); };
  const markAllRead = async () => { await api.post("/notifications/read-all"); setNotifs((arr) => arr.map((n) => ({ ...n, read: true }))); };
  const setLang = (code) => { i18n.changeLanguage(code); localStorage.setItem("hk_lang", code); setLangOpen(false); };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--theme-bg, #F8FAFC)" }}>
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen" data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-2.5">
          {brandLogo ? (
            <img src={brandLogo} alt="brand" className="w-9 h-9 rounded-md object-cover border border-slate-200" />
          ) : (
            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--theme-primary, #2563EB)" }}>
              <Hospital weight="fill" size={20} className="text-white" />
            </div>
          )}
          <div className="leading-tight">
            <div className="font-extrabold text-slate-900 text-[15px] tracking-tight" style={{ fontFamily: "Manrope" }}>{brandDisplay || t("app_name")}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("app_tagline")}</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <div className="label-small px-3 py-2">{t("workspace")}</div>
          {nav.map((n) => (
            <NavLink
              to={n.to}
              key={n.to}
              data-testid={`nav-${n.labelKey}`}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              end={n.to === "/"}
            >
              <n.icon size={18} weight="regular" />
              <span>{t(n.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 font-bold">
              {user?.name?.[0] || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold truncate">{user?.custom_role || roleLabel(user?.role)}</div>
            </div>
            <button onClick={onSignOut} title={t("sign_out")} data-testid="sidebar-signout-button" className="p-2 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 h-16 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <span className="label-small">{t("context")}</span>
            <div className="relative">
              <button
                onClick={() => setContextOpen((o) => !o)}
                data-testid="context-selector-button"
                className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-md hover:border-slate-300 transition-colors bg-white text-sm font-medium text-slate-900"
              >
                <Hospital size={15} weight="regular" />
                <span>{selectedHospital ? (selectedHospital.display_name || selectedHospital.name) : t("all_hospitals")}</span>
                <CaretDown size={12} />
              </button>
              {contextOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-slate-200 rounded-md shadow-sm z-40" data-testid="context-selector-dropdown">
                  {(!user?.hospital_ids?.length) && (
                    <button
                      onClick={() => { setSelectedHospitalId(""); localStorage.removeItem("hk_hospital"); setContextOpen(false); window.dispatchEvent(new Event("hk-context-changed")); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100"
                    >
                      {t("all_hospitals")}
                    </button>
                  )}
                  {hospitals.map((h) => (
                    <button
                      key={h.id}
                      data-testid={`context-hospital-${h.id}`}
                      onClick={() => { setSelectedHospitalId(h.id); localStorage.setItem("hk_hospital", h.id); setContextOpen(false); window.dispatchEvent(new Event("hk-context-changed")); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex items-center justify-between"
                    >
                      <span>{h.display_name || h.name}</span>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500">{h.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setLangOpen((o) => !o)}
                data-testid="lang-toggle-button"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 transition-colors text-sm text-slate-700"
              >
                <Translate size={15} weight="regular" />
                <span className="uppercase text-xs font-bold">{i18n.language}</span>
                <CaretDown size={11} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-1 w-44 bg-white border border-slate-200 rounded-md shadow-sm z-40 max-h-72 overflow-y-auto" data-testid="lang-dropdown">
                  {LANGUAGES.map((l) => (
                    <button key={l.code} data-testid={`lang-opt-${l.code}`} onClick={() => setLang(l.code)} className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 hover:bg-slate-50 flex items-center justify-between ${i18n.language === l.code ? "bg-blue-50/50 font-semibold" : ""}`}>
                      <span>{l.label}</span>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500">{l.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setNotifOpen((o) => !o)}
                data-testid="notifications-toggle-button"
                className="relative p-2 rounded-md border border-slate-200 hover:border-slate-300 transition-colors"
              >
                <Bell size={18} weight="regular" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center" style={{ background: "var(--theme-primary, #2563EB)" }}>
                    {unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-slate-200 rounded-md shadow-sm z-40" data-testid="notifications-dropdown">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                    <div className="text-sm font-bold text-slate-900">{t("notifications")}</div>
                    <button onClick={markAllRead} className="text-xs text-blue-600 font-semibold hover:underline">{t("mark_all_read")}</button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.length === 0 ? (
                      <div className="p-6 text-center text-sm text-slate-500">{t("no_notifications")}</div>
                    ) : notifs.slice(0, 20).map((n) => (
                      <Link to={n.link || "#"} key={n.id} onClick={() => setNotifOpen(false)} className={`block px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 ${!n.read ? "bg-blue-50/40" : ""}`}>
                        <div className="text-sm font-semibold text-slate-900">{n.title}</div>
                        <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 md:p-8 max-w-[1500px] w-full mx-auto" data-testid="main-content">
          <Outlet context={{ selectedHospitalId }} />
        </main>
      </div>
    </div>
  );
}

export function useSelectedHospital() {
  const [id, setId] = useState(() => localStorage.getItem("hk_hospital") || "");
  useEffect(() => {
    const onChange = () => setId(localStorage.getItem("hk_hospital") || "");
    window.addEventListener("hk-context-changed", onChange);
    return () => window.removeEventListener("hk-context-changed", onChange);
  }, []);
  return id;
}
